import {
    createContext,
    useContext,
    useState,
    useEffect,
    ReactNode,
} from "react";
import { useUser } from "./UserContext.js";
import {
    AvatarCustomization,
    BannerData,
    FontStyle,
    ItemData,
    Purchase,
} from "@mythic/pixl";

import { usePixl } from "./PixlContext.js";

export type CustomizationContextType = {
    font: FontStyle | null;
    setFont: React.Dispatch<React.SetStateAction<FontStyle | null>>;

    banner: BannerData | null;
    setBanner: React.Dispatch<React.SetStateAction<BannerData | null>>;

    customization: AvatarCustomization | null;
    setCustomization: React.Dispatch<
        React.SetStateAction<AvatarCustomization | null>
    >;

    purchases: Purchase[];
    setPurchases: React.Dispatch<React.SetStateAction<Purchase[]>>;

    changesMade: boolean;
    isTryingOn: boolean;
    saveChanges: () => void;
    resetChanges: () => void;

    categoryMap: {
        key: string;
        method: string;
        allowNull: boolean;
    }[];
};

const CustomizationContext = createContext<CustomizationContextType | null>(
    null,
);

export const useCustomization = (): CustomizationContextType => {
    const context = useContext(CustomizationContext);
    if (!context)
        throw new Error(
            "useCustomization must be used with in a CustomizationProvider",
        );
    return context;
};

interface Props {
    children: ReactNode;
}

export const CustomizationProvider = ({ children }: Props) => {
    const user = useUser()?.user;
    const { pixlDb } = usePixl();

    const [font, setFont] = useState<FontStyle | null>(null);
    const [originalFont, setOriginalFont] = useState("");

    const [banner, setBanner] = useState<BannerData | null>(null);
    const [originalBanner, setOriginalBanner] = useState("");

    const [customization, setCustomization] =
        useState<AvatarCustomization | null>(null);
    const [originalCustomization, setOriginalCustomization] = useState("");

    const [purchases, setPurchases] = useState([] as Purchase[]);
    const [changesMade, setChangesMade] = useState(false);
    const [isTryingOn, setIsTryingOn] = useState(false);

    const categoryMap = [
        { method: "getHeads", key: "text", allowNull: false },
        { method: "getBanners", key: "banner", allowNull: true },
        { method: "getHeads", key: "head", allowNull: false },
        { method: "getEyes", key: "eyes", allowNull: true },
        { method: "getMouths", key: "mouth", allowNull: true },
        { method: "getBodies", key: "body", allowNull: false },
        { method: "getClothes", key: "clothes", allowNull: false },
        { method: "getHeadAccs", key: "head_acc", allowNull: true },
        { method: "getFaceAccs", key: "face_acc", allowNull: true },
        { method: "getBodyAccs", key: "body_acc", allowNull: true },
        { method: "getBackAccs", key: "back_acc", allowNull: true },
        { method: "getEffects", key: "effects", allowNull: false },
    ];

    const save = (data: AvatarCustomization, purchases: Purchase[] = []) => {
        if (!user) return;

        const opts: RequestInit = {
            method: "POST",
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                avatar: data,
                purchases,
            }),
        };

        fetch(
            `${import.meta.env.VITE_SERVER_URL || ""}/api/avatars/${user.id}/save`,
            opts,
        )
            .then((res) => {
                return res.json();
            })
            .then((json) => {
                console.log("Save status: ", json);

                // Check if the status is OK
                if (json.success) {
                    // Set the customization values.
                    if (customization)
                        setOriginalCustomization(JSON.stringify(customization));
                    if (font) setOriginalFont(JSON.stringify(font));
                    if (banner) setOriginalBanner(JSON.stringify(banner));
                }
            })
            .catch((err) => console.error("Failed to log out:", err));
    };

    // This is a publicly available wrapper for the save method.
    const saveChanges = () => {
        if (changesMade && !isTryingOn) {
            // Deep clone the customization data.
            const saveData = JSON.parse(JSON.stringify(customization));

            // Set the font data
            saveData.font = font;

            // Set the banner data
            saveData.banner = banner;

            save(saveData, purchases);
        }
    };

    const resetChanges = () => {
        if (changesMade) {
            setCustomization(JSON.parse(originalCustomization));
            setFont(JSON.parse(originalFont));
            setBanner(JSON.parse(originalBanner));
        }
    };

    const value = {
        font,
        setFont,
        banner,
        setBanner,
        customization,
        setCustomization,
        purchases,
        setPurchases,
        changesMade,
        isTryingOn,
        saveChanges,
        resetChanges,
        categoryMap,
    };

    /*
        The below effect takes the Twitch user information and fetches
        avatar data from the server using user.id

        If the user doesn't have existing character information, new
        character information will be generated and posted as their
        new default.
    */
    useEffect(() => {
        if (!user || !user.id) return;

        // Fetch the data
        fetch(
            `${import.meta.env.VITE_SERVER_URL || ""}/api/avatars/${user.id}/get`,
        )
            .then((f) => {
                try {
                    return f.json();
                } catch (err) {
                    console.log(err);
                    return null;
                }
            })
            .then((json) => {
                // If there is no data
                if (!json || JSON.stringify(json) === "{}") {
                    if (!pixlDb) return;
                    const randDesign = pixlDb?.getRandomDesign();

                    // Set the states
                    setCustomization(randDesign);
                    setOriginalCustomization(JSON.stringify(randDesign));

                    setFont(randDesign.font);
                    setOriginalFont(JSON.stringify(randDesign.font));

                    setBanner(randDesign.banner);
                    setOriginalBanner(JSON.stringify(randDesign.banner));

                    setPurchases([] as Purchase[]);

                    save(randDesign);
                    return;
                }

                if ("avatar" in json) {
                    const avatar = json.avatar;

                    if ("font" in avatar) {
                        setFont({ ...avatar.font });
                        setOriginalFont(JSON.stringify(avatar.font));
                    }
                    if ("banner" in avatar) {
                        setBanner(avatar.banner);
                        setOriginalBanner(JSON.stringify(avatar.banner));
                    }

                    setCustomization(avatar);

                    // Turn the JSON into a string for comparison later.
                    setOriginalCustomization(JSON.stringify(avatar));
                }
                setPurchases("purchases" in json ? json.purchases : []);
            });
    }, [user]);

    /*
        The effect below is used to calculate if any changes have been made.
        It deliberately leaves out changes to purchases as these do not
        qualify as changes to the customization data.
    */
    useEffect(() => {
        if (!customization) return;

        let changes: boolean =
            JSON.stringify(customization) !== originalCustomization ||
            JSON.stringify(font) !== originalFont ||
            JSON.stringify(banner) !== originalBanner;

        setChangesMade(changes);

        // If changes have been made, verify the save file only
        // contains items that are permitted.
        if (changes) {
            // Get the fully qualified avatar data
            const translated = pixlDb?.translateAvatarData(customization);

            // Go through every key in the save data and verify that it is either
            // free or owned by the user.
            for (const key in translated) {
                // If the item is premium
                if (translated[key].isPremium) {
                    // If there are no purchases but there is a premium item equipped
                    // the user clearly does not own the item and it can be cancelled.
                    if (!purchases || purchases.length === 0) {
                        setIsTryingOn(true);
                        return;
                    }

                    // Check the purchases list for the given item. Both id and category
                    // are checked here to ensure that there are no false positives.
                    const owned = purchases.find(
                        (i) =>
                            i.id === translated[key].id && i.category === key,
                    );

                    // The user does not own an item and cannot save.
                    if (!owned) {
                        setIsTryingOn(true);
                        return;
                    }
                }
            }
            // Check the banner next

            // Get the banner ItemData
            const bannerItem = pixlDb
                ?.getBanners()
                ?.find((b) => b.id === banner?.id);

            // Check if the banner is premium
            if (bannerItem?.isPremium) {
                if (!purchases) {
                    setIsTryingOn(true);
                    return;
                }
                const bannerOwned = purchases?.find(
                    (b) => b.category === "banner" && b.id === banner?.id,
                );
                if (!bannerOwned) {
                    setIsTryingOn(true);
                    return;
                }
            }
        }

        setIsTryingOn(false);
    }, [
        customization,
        originalCustomization,
        font,
        originalFont,
        banner,
        originalBanner,
        purchases,
    ]);

    return (
        <CustomizationContext.Provider value={value}>
            {children}
        </CustomizationContext.Provider>
    );
};
